import { useRef, useState } from "preact/hooks";
import { api } from "../api";
import { colorFor, initials } from "../util";
import { Icon } from "./Icon";

export function Avatar({ url, jid, name, size = 49, group }: { url?: string | null; jid: string; name: string; size?: number; group?: boolean }) {
  const [broken, setBroken] = useState(false);
  // Preact 11 no longer appends "px" to numeric style values.
  const style = { width: `${size}px`, height: `${size}px`, fontSize: `${Math.round(size * 0.4)}px` };
  if (url && !broken) return <img class="avatar" src={url} alt="" style={style} onError={() => setBroken(true)} loading="lazy" />;
  return (
    <div class="avatar avatar-fallback" style={{ ...style, background: colorFor(jid) }} aria-hidden="true">
      {group ? <Icon name="group" size={size * 0.6} /> : !name.trim() || /^\+?\d/.test(name) ? <Icon name="person" size={size * 0.6} /> : initials(name)}
    </div>
  );
}

/** Square-crop and upload an image for use as an avatar. */
export async function uploadAvatar(file: File) {
  const small = await downscale(file, 640);
  return api.upload(small, "avatar.jpg");
}

/** Click-to-change photo. By default saves it as the user's profile photo;
 * `save` can store it elsewhere (group icon) and return the new URL. */
export function AvatarPicker({
  url,
  jid,
  name,
  onChange,
  save,
  group,
  disabled,
}: {
  url: string | null;
  jid: string;
  name: string;
  onChange: (url: string | null) => void;
  save?: (mediaId: string, url: string) => Promise<string | null>;
  group?: boolean;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function pick(file: File) {
    setBusy(true);
    try {
      const up = await uploadAvatar(file);
      onChange(save ? await save(up.id, up.url) : (await api.updateMe({ avatar_media_id: up.id })).avatar_url);
    } catch (e) {
      alertLater(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button type="button" class="avatar-picker" onClick={() => !disabled && input.current?.click()} disabled={busy} aria-label={group ? "Change group icon" : "Change profile photo"}>
      <Avatar url={url} jid={jid} name={name} size={180} group={group} />
      <span class="avatar-picker-overlay" hidden={disabled}>
        <Icon name="camera" size={24} />
        {busy ? "Uploading…" : url ? "Change photo" : "Add photo"}
      </span>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.currentTarget.files?.[0];
          if (f) void pick(f);
          e.currentTarget.value = "";
        }}
      />
    </button>
  );
}

/** Center-crop to a square JPEG of at most `max` px. */
async function downscale(file: File, max: number): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const side = Math.min(bmp.width, bmp.height);
  const out = Math.min(side, max);
  const canvas = new OffscreenCanvas(out, out);
  canvas.getContext("2d")!.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, out, out);
  bmp.close();
  return canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
}

function alertLater(e: unknown) {
  console.error(e);
  queueMicrotask(() => window.dispatchEvent(new CustomEvent("xmm-toast", { detail: e instanceof Error ? e.message : String(e) })));
}
